// Copyright 2024 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, {useEffect, useRef} from "react";
import * as Util from "./Util";

// TelegramLoginButton renders the official Telegram Login Widget
// https://core.telegram.org/widgets/login
// size prop: "small" | "medium" | "large" (default: "large")
const TelegramLoginButton = ({provider, application, method, size = "large"}) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!provider?.clientId) {
      return;
    }

    // Build state parameter for callback
    const state = Util.getStateFromQueryParams(
      application?.name || "app-built-in",
      provider.name,
      method || "signup"
    );

    // Build auth URL - Telegram will redirect here after authentication
    const authUrl = `${window.location.origin}/callback?state=${state}`;

    const script = document.createElement("script");
    script.src = "https://telegram.org/js/telegram-widget.js?22";
    script.async = true;
    script.setAttribute("data-telegram-login", provider.clientId);
    // Telegram widget supports: "large", "medium", "small"
    script.setAttribute("data-size", size === "small" ? "small" : "large");
    script.setAttribute("data-auth-url", authUrl);
    script.setAttribute("data-request-access", "write");

    if (containerRef.current) {
      // Clear any existing content
      containerRef.current.innerHTML = "";
      containerRef.current.appendChild(script);
    }

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
    };
  }, [provider, application, method, size]);

  if (!provider?.clientId) {
    return null;
  }

  return <div ref={containerRef} style={{display: "flex", justifyContent: "center"}} />;
};

export default TelegramLoginButton;
